import { ContagemRegressiva } from "@/componentes/ContagemRegressiva";
import { getBandeiraUrl } from "../utils/bandeiras";
import { Trash2, Edit2 } from "lucide-react";

function formatarData(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function CartaoJogo({ jogo, palpiteAtual, onPalpitar, onDeletar, onEditar, modo = "apostador", testId }) {
  const encerrado = jogo.resultadoLancado || jogo.statusJogo === "ENCERRADO";
  const podePalpitar = !encerrado && new Date(jogo.dataLimitePalpite).getTime() > Date.now();
  const mandante = jogo.selecaoMandante || {};
  const visitante = jogo.selecaoVisitante || {};
  const grupo = jogo.grupo || {};

  return (
    <article
      data-testid={testId || `cartao-jogo-${jogo.id}`}
      className="surface-card rounded-2xl p-6 transition-all duration-300 hover:border-verdebrasil/40 hover:-translate-y-0.5 hover:glow-green relative overflow-hidden"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">
          {grupo.nome || "Grupo"} • Rodada {jogo.rodada}
        </span>
        {encerrado ? (
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/60 bg-white/5 px-2 py-1 rounded-full">
            {jogo.statusJogo}
          </span>
        ) : (
          <ContagemRegressiva alvoIso={jogo.dataLimitePalpite} testId={`countdown-${jogo.id}`} />
        )}
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 mb-4">
        <div className="text-center">
          <div className="text-4xl mb-1">
            {mandante.sigla && getBandeiraUrl(mandante.sigla) ? (
              <img src={getBandeiraUrl(mandante.sigla)} alt={mandante.sigla} className="w-12 h-auto mx-auto rounded" />
            ) : (
              <span className="text-sm font-bold">{mandante.sigla || "?"}</span>
            )}
          </div>
          <div className="font-heading uppercase text-sm tracking-wide font-bold truncate">{mandante.nome}</div>
          <div className="text-[10px] tracking-[0.3em] text-white/40 uppercase">{mandante.sigla}</div>
        </div>
        <div className="text-center">
          {encerrado && jogo.golsMandante != null ? (
            <div className="font-heading font-black text-5xl text-amarelobrasil tracking-tighter">
              {jogo.golsMandante}<span className="text-white/30 mx-1">×</span>{jogo.golsVisitante}
            </div>
          ) : (
            <div className="font-heading font-bold text-xl text-white/40">VS</div>
          )}
          <div className="text-[10px] text-white/40 mt-1">{formatarData(jogo.dataHoraJogo)}</div>
        </div>
        <div className="text-center">
          <div className="text-4xl mb-1">
            {visitante.sigla && getBandeiraUrl(visitante.sigla) ? (
              <img src={getBandeiraUrl(visitante.sigla)} alt={visitante.sigla} className="w-12 h-auto mx-auto rounded" />
            ) : (
              <span className="text-sm font-bold">{visitante.sigla || "?"}</span>
            )}
          </div>
          <div className="font-heading uppercase text-sm tracking-wide font-bold truncate">{visitante.nome}</div>
          <div className="text-[10px] tracking-[0.3em] text-white/40 uppercase">{visitante.sigla}</div>
        </div>
      </div>

      {modo === "apostador" && (
        <div className="border-t border-white/5 pt-4">
          {palpiteAtual ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold">Seu palpite</div>
                <div className="font-heading font-black text-2xl mt-1">
                  {palpiteAtual.golsMandantePalpite}<span className="text-white/30 mx-2">×</span>{palpiteAtual.golsVisitantePalpite}
                </div>
                {encerrado && (
                  <div className="mt-1 text-xs font-bold uppercase tracking-widest text-verdebrasil">
                    {palpiteAtual.pontosGerados} pts
                  </div>
                )}
              </div>
              {podePalpitar && (
                <button
                  onClick={() => onPalpitar?.(jogo, palpiteAtual)}
                  className="px-5 py-2.5 rounded-md font-heading uppercase tracking-widest text-xs bg-transparent border border-amarelobrasil text-amarelobrasil hover:bg-amarelobrasil hover:text-superficie-base transition-all"
                  data-testid={`btn-editar-palpite-${jogo.id}`}
                >
                  Editar
                </button>
              )}
            </div>
          ) : podePalpitar ? (
            <button
              onClick={() => onPalpitar?.(jogo)}
              className="w-full py-3 rounded-md font-heading uppercase tracking-widest text-sm bg-verdebrasil hover:bg-verdebrasil-dark text-white transition-all hover:glow-green"
              data-testid={`btn-fazer-palpite-${jogo.id}`}
            >
              Fazer Palpite
            </button>
          ) : (
            <div className="text-center text-xs uppercase tracking-widest text-white/40 py-2">
              Prazo encerrado
            </div>
          )}
        </div>
      )}

      {modo === "admin" && (
        <div className="border-t border-white/5 pt-4 flex justify-between items-center gap-2">
          <div className="flex gap-2">
            {onEditar && (
              <button
                onClick={() => onEditar(jogo)}
                className="p-2.5 rounded-md bg-amarelobrasil/20 hover:bg-amarelobrasil/40 text-amarelobrasil hover:text-amarelobrasil transition-all"
                data-testid={`btn-editar-jogo-${jogo.id}`}
                title="Editar partida"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            {onDeletar && (
              <button
                onClick={() => {
                  if (window.confirm("Deseja mesmo deletar a partida?")) {
                    onDeletar(jogo);
                  }
                }}
                className="p-2.5 rounded-md bg-red-600/20 hover:bg-red-600/40 text-red-500 hover:text-red-400 transition-all"
                data-testid={`btn-deletar-jogo-${jogo.id}`}
                title="Deletar partida"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
          <button
            onClick={() => onPalpitar?.(jogo)}
            className="px-5 py-2.5 rounded-md font-heading uppercase tracking-widest text-xs bg-verdebrasil hover:bg-verdebrasil-dark text-white transition-all"
            data-testid={`btn-lancar-resultado-${jogo.id}`}
          >
            {encerrado ? "Corrigir Resultado" : "Lançar Resultado"}
          </button>
        </div>
      )}

      <div className="absolute top-0 left-0 h-1 w-full stripe-brasil opacity-50" />
    </article>
  );
}

